import { useMemo } from 'react';
import { useSelector } from '../redux/store';

/**
 * Shared hook to derive practitioners and assignable users from Redux practiceUser state.
 * Eliminates duplicated useMemo logic across TreatmentAndAppointment, AssignedToLead, etc.
 */
const usePracticeStaff = () => {
    const { practiceUser } = useSelector((state) => state.practiceUser);

    const practitioners = useMemo(() => {
        if (!practiceUser?.results?.length) return [];
        return practiceUser.results
            .filter(el => el?.is_enabled && el?.role?.toUpperCase() === 'PRACTITIONER' && !el?.user?.is_superuser)
            .map(el => ({
                ...el,
                _name: `${el?.user?.first_name || ''} ${el?.user?.last_name || ''}`.trim(),
                _initials: ((el?.user?.first_name?.[0] || '') + (el?.user?.last_name?.[0] || '')).toUpperCase() || '?',
            }));
    }, [practiceUser]);

    const assignableUsers = useMemo(() => {
        if (!practiceUser?.results?.length) return [];
        return practiceUser.results
            .filter(el => el?.is_enabled && ['TCO', 'USER'].includes(el?.role?.toUpperCase()) && !el?.user?.is_superuser)
            .map(el => ({
                ...el,
                _name: `${el?.user?.first_name || ''} ${el?.user?.last_name || ''}`.trim(),
                _initials: ((el?.user?.first_name?.[0] || '') + (el?.user?.last_name?.[0] || '')).toUpperCase() || '?',
            }));
    }, [practiceUser]);

    const bookableStaff = useMemo(() => {
        if (practitioners.length > 0) return practitioners;
        if (!practiceUser?.results) return [];
        return practiceUser.results
            .filter(el => {
                const role = el?.role?.toUpperCase();
                return el?.is_enabled && role !== 'ADMIN' && role !== 'SUPPORT' && !el?.user?.is_superuser;
            })
            .map(el => ({
                ...el,
                _name: `${el?.user?.first_name || ''} ${el?.user?.last_name || ''}`.trim(),
                _initials: ((el?.user?.first_name?.[0] || '') + (el?.user?.last_name?.[0] || '')).toUpperCase() || '?',
            }));
    }, [practitioners, practiceUser]);

    return { practitioners, assignableUsers, bookableStaff, isLoaded: !!practiceUser?.results };
};

export default usePracticeStaff;
